/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box, Stack } from '@mui/material';
import { format } from 'bytes';
import styled from 'styled-components';
import { VictoryLabel, VictoryPie } from 'victory';
import appTheme from '../../common/theme/appTheme.json';
import { isSafari } from 'react-device-detect';
//icons
import { isNil } from 'lodash';
import DataUsageChartContainer from './DataUsageChartContainer';
import { PlaceholderHeading, PlaceholderText } from '../DataUsageChartsCommonComponents';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';

interface SolidCircleProps {
  linkType: string;
}
interface NetworkUsageChartProps {
  rlTotalUsage: number | undefined;
  flTotalUsage: number | undefined;

  width: number;
}
const TotalLinkUsageChart = (props: NetworkUsageChartProps) => {
  const { rlTotalUsage, flTotalUsage, width } = props;
  const noData = isNil(rlTotalUsage) || isNil(flTotalUsage);
  return (
    <DataUsageChartContainer width={width} header="Link Usage (Comparison)">
      <Box
        sx={{
          backgroundColor: appTheme.primaryBackgroundColor,
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
        }}
      >
        {/* chart body */}
        <Stack direction="row" spacing={2} sx={{ height: '383px' }}>
          {/* Pie chart */}
          <Box
            sx={{
              width: '60%',
              display: 'flex',
              alignIems: 'center',
              justifyContent: 'center',
              'svg:not(:root)': { overflow: isSafari ? 'visible' : 'hidden', paddingLeft: isSafari ? '12px' : '0px' },
            }}
          >
            <svg viewBox="0 0 400 400">
              <VictoryPie
                standalone={false}
                width={400}
                height={400}
                radius={isSafari ? 155 : 170}
                innerRadius={noData ? (isSafari ? 151 : 166) : isSafari ? 121 : 136}
                startAngle={55}
                endAngle={415}
                padAngle={1}
                labels={() => null}
                data={
                  noData
                    ? [{ x: undefined, y: 1 }]
                    : [
                        { x: 'Return Link', y: rlTotalUsage },
                        { x: 'Forward Link', y: flTotalUsage },
                      ]
                }
                style={{
                  data: {
                    fill: ({ datum }) =>
                      isNil(datum.x)
                        ? appTheme.mptPrimaryBackgroundColor
                        : datum.x === 'Return Link'
                        ? '#9BE1FF'
                        : '#14B9FF',
                  },
                }}
              />
              <VictoryLabel
                textAnchor="middle"
                style={{
                  fontSize: '52px',
                  fontWeight: '400',
                  lineHeight: '56px',
                  fontFamily: appTheme.viasatFontFamilyRegular,
                  fill: appTheme.primaryTextColor,
                  letterSpacing: '1px',
                }}
                x={200}
                y={200}
                text={noData ? '' : format(rlTotalUsage + flTotalUsage).toLocaleString()}
              />
              <VictoryLabel
                textAnchor="middle"
                style={{
                  fontSize: '20px',
                  fontWeight: '400',
                  lineHeight: '20px',
                  fontFamily: appTheme.viasatFontFamilyRegular,
                  fill: appTheme.primaryTextColor,
                  visibility: noData ? 'hidden' : 'visible',
                }}
                x={200}
                y={255}
                text="Total"
              />
            </svg>
            {noData && (
              <Box
                sx={{
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  height: '364px',
                  width: '170px',
                }}
              >
                <PlaceholderHeading>No Terminals</PlaceholderHeading>
                <PlaceholderText>To view data usage analytics, select one or multiple terminals</PlaceholderText>
              </Box>
            )}
          </Box>
          {/* arclight and viasat networks */}
          <Stack spacing={3} sx={{ width: '40%', alignItems: 'flex-start', justifyContent: 'center' }}>
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
              <SolidCircle networkType="Return Link" />
              <Stack>
                <StyledNetworkType>Return Link</StyledNetworkType>
                <StyledNetworkUsageValue>
                  {isNil(rlTotalUsage) ? EMPTY_FIELD_STRING_PLACEHOLDER : format(rlTotalUsage).toLocaleString()}
                </StyledNetworkUsageValue>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
              <SolidCircle />
              <Stack>
                <Stack direction="row" sx={{ height: '20px' }}>
                  <StyledNetworkType>Forward Link</StyledNetworkType>
                </Stack>

                <StyledNetworkUsageValue>
                  {isNil(flTotalUsage) ? EMPTY_FIELD_STRING_PLACEHOLDER : format(flTotalUsage).toLocaleString()}
                </StyledNetworkUsageValue>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </DataUsageChartContainer>
  );
};

export default TotalLinkUsageChart;

export const StyledNetworkType = styled.h5`
  font-family: ${appTheme.viasatFontFamilyRegular};
  color: #9fafbc;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin: 0px;

  @media print {
    color: black;
    margin: 0px;
  }
`;
export const StyledNetworkUsageValue = styled.h4`
  font-family: ${appTheme.viasatFontFamilyRegular};
  color: ${appTheme.primaryTextColor};
  font-size: 24px;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0.2px;
  margin: 0px;

  @media print {
    color: black;
    margin: 0px;
  }
`;

export const SolidCircle = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 12px;
  background-color: ${({ linkType }: SolidCircleProps) => (linkType === 'Return Link' ? '#9BE1FF' : '#14B9FF')};
  margin-top: 3px;

  @media print {
    color: black;
    margin: 0px;
  }
`;
